import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { PRICE_CONFIG, SHIPMENT_INSURANCE_TYPE } from '@/features/firstMile/constants';
import { updateValueProtection } from '@/features/firstMile/slices/quotationAndShippingSlice';

import type { QtdShp } from '@/services/first-mile/quotation-and-shipping/shipmentQuotationApi';

export interface PricedAddOn {
    curCd: string;
    amtVal: number;
    isInsurance?: boolean;
    // Value protection the insurance price was quoted with
    pricedWith?: { insuredValue: string; totalDeclaredValue: string; currency: string };
}

const toComparableAmount = (value: unknown) => Number(String(value ?? '').replace(/,/g, '')) || 0;

export interface ShipmentSelectionState {
    selectedOptionId: string | null; // Maps to qtdShpId
    expandedCardId: string | null; // Tracks open accordion
    checkedAddOns: Record<string, string[]>; // Clean whitelist tracker: { [qtdShpId]: ['chrgSrvCd1', 'chrgSrvCd2'] }
    protectionValues: Record<string, string>; // { [qtdShpId]: 'value' }
    shipmentDate: string | null; // User-selected shipment date
    // Add-on prices fetched via "Update Price": { [qtdShpId]: { [chrgSrvCd]: price } }
    pricedAddOns: Record<string, Record<string, PricedAddOn>>;
    needsRepricing?: Record<string, boolean>;
    refreshedOptions?: Record<
        string,
        { option: QtdShp; promoOption: QtdShp | null; checkedCodes: string[] }
    >;
}

const initialState: ShipmentSelectionState = {
    selectedOptionId: null,
    expandedCardId: null,
    checkedAddOns: {},
    protectionValues: {},
    shipmentDate: null,
    pricedAddOns: {},
    needsRepricing: {},
    refreshedOptions: {},
};

const shipmentSelectionSlice = createSlice({
    name: 'shipmentSelection',
    initialState,
    reducers: {
        selectShippingOption(
            state,
            action: PayloadAction<{ optionId: string; defaultCheckedCodes: string[] }>,
        ) {
            const { optionId, defaultCheckedCodes } = action.payload;
            state.selectedOptionId = optionId;
            state.expandedCardId = optionId;

            // Only initialize the array if the user has never interacted with this card option before
            if (!state.checkedAddOns[optionId]) {
                state.checkedAddOns[optionId] = defaultCheckedCodes;
            }
        },
        toggleCardExpansion(state, action: PayloadAction<string>) {
            state.expandedCardId = state.expandedCardId === action.payload ? null : action.payload;
        },
        // Intuitive whitelist toggle: pushes if missing, removes if present
        toggleAddOnSelection(
            state,
            action: PayloadAction<{ optionId: string; chrgSrvCd: string }>,
        ) {
            const { optionId, chrgSrvCd } = action.payload;
            state.needsRepricing ??= {};
            state.needsRepricing[optionId] = true;

            if (!state.checkedAddOns[optionId]) {
                state.checkedAddOns[optionId] = [];
            }

            const idx = state.checkedAddOns[optionId].indexOf(chrgSrvCd);
            if (idx > -1) {
                state.checkedAddOns[optionId].splice(idx, 1);
            } else {
                state.checkedAddOns[optionId].push(chrgSrvCd);
            }
        },
        updateProtectionValue(state, action: PayloadAction<{ optionId: string; value: string }>) {
            const { optionId, value } = action.payload;
            state.protectionValues[optionId] = value;
        },
        setShipmentDate(state, action: PayloadAction<string | null>) {
            if (state.shipmentDate !== action.payload) {
                state.refreshedOptions = {};
                state.pricedAddOns = {};
            }
            state.shipmentDate = action.payload;
        },

        setRefreshedOption(
            state,
            action: PayloadAction<{
                optionId: string;
                option: QtdShp;
                promoOption: QtdShp | null;
                checkedCodes: string[];
            }>,
        ) {
            const { optionId, ...refresh } = action.payload;
            state.refreshedOptions ??= {};
            state.refreshedOptions[optionId] = refresh;
            const availableCodes = new Set(
                refresh.option.extrChrgSrv
                    .filter((charge) => charge.chrgCdTyCd === PRICE_CONFIG.XCH)
                    .map((charge) => charge.chrgSrvCd),
            );
            const currentCodes =
                state.checkedAddOns[optionId]?.filter((code) => availableCodes.has(code)) ??
                refresh.checkedCodes;
            state.needsRepricing ??= {};
            state.needsRepricing[optionId] =
                currentCodes.length !== refresh.checkedCodes.length ||
                currentCodes.some((code) => !refresh.checkedCodes.includes(code));
        },

        setPricedAddOns(
            state,
            action: PayloadAction<{ optionId: string; prices: Record<string, PricedAddOn> }>,
        ) {
            const { optionId, prices } = action.payload;
            state.pricedAddOns[optionId] = prices;
        },

        toggleInsuranceGlobal(state, action: PayloadAction<{ isChecked: boolean }>) {
            const { isChecked } = action.payload;
            Object.keys(state.checkedAddOns).forEach((optionId) => {
                const currentArray = state.checkedAddOns[optionId];
                if (!currentArray) return;
                const excludeInsurance = currentArray.filter(
                    (item) => item !== SHIPMENT_INSURANCE_TYPE,
                );
                if (
                    currentArray.includes(SHIPMENT_INSURANCE_TYPE) !== isChecked &&
                    state.refreshedOptions?.[optionId]?.option.extrChrgSrv.some(
                        (charge) => charge.chrgSrvCd === SHIPMENT_INSURANCE_TYPE,
                    )
                ) {
                    state.needsRepricing ??= {};
                    state.needsRepricing[optionId] = true;
                }
                state.checkedAddOns[optionId] = isChecked
                    ? [...excludeInsurance, SHIPMENT_INSURANCE_TYPE]
                    : excludeInsurance;
            });
        },

        removeSpecificAddOns(state, action: PayloadAction<{ addOnCodes: string[] }>) {
            if (!state.checkedAddOns || Object.keys(state.checkedAddOns).length === 0) {
                return;
            }

            const { addOnCodes } = action.payload;

            Object.keys(state.checkedAddOns).forEach((optionId) => {
                const currentArray = state.checkedAddOns[optionId];
                if (!currentArray) return;

                if (currentArray.some((code) => addOnCodes.includes(code))) {
                    state.needsRepricing ??= {};
                    state.needsRepricing[optionId] = true;
                }
                state.checkedAddOns[optionId] = currentArray.filter(
                    (item) => !addOnCodes.includes(item),
                );
            });
        },

        clearShipmentSelection(state) {
            state.selectedOptionId = null;
            state.expandedCardId = null;
            state.checkedAddOns = {};
            state.protectionValues = {};
            state.pricedAddOns = {};
            state.refreshedOptions = {};
            state.needsRepricing = {};
        },
    },
    extraReducers: (builder) => {
        // Insurance price depends on the insured/declared value, so drop it only when that value changes
        builder.addCase(updateValueProtection, (state, action) => {
            const { insuredValue, totalDeclaredValue, currency } = action.payload;
            if (
                insuredValue === undefined &&
                totalDeclaredValue === undefined &&
                currency === undefined
            ) {
                return;
            }

            Object.entries(state.pricedAddOns).forEach(([optionId, prices]) => {
                Object.keys(prices).forEach((code) => {
                    const entry = prices[code];
                    if (!entry?.isInsurance) return;

                    const pricedWith = entry.pricedWith;
                    const isChanged =
                        !pricedWith ||
                        (insuredValue !== undefined &&
                            toComparableAmount(insuredValue) !==
                                toComparableAmount(pricedWith.insuredValue)) ||
                        (totalDeclaredValue !== undefined &&
                            toComparableAmount(totalDeclaredValue) !==
                                toComparableAmount(pricedWith.totalDeclaredValue)) ||
                        (currency !== undefined && (currency ?? '') !== pricedWith.currency);

                    if (isChanged) {
                        delete prices[code];
                        delete state.refreshedOptions?.[optionId];
                    }
                });
            });
        });
    },
});

export const {
    selectShippingOption,
    toggleCardExpansion,
    toggleAddOnSelection,

    setShipmentDate,
    setPricedAddOns,
    setRefreshedOption,
    toggleInsuranceGlobal,
    removeSpecificAddOns,
    clearShipmentSelection,
} = shipmentSelectionSlice.actions;

export default shipmentSelectionSlice.reducer;
